import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';

/**
 * CartApiService (Angular 18) — shopping cart, items, and checkout.
 *
 * Replaces apps/b2b-ui/common/services/api/cartApi/cartApi.service.js.
 *
 * Legacy quirks preserved 1:1:
 *   - `list()` and `detail(id)` both hit `/v2/api/carts` (without an id).
 *     `detail(id)` is documented in the legacy interface but ignores the id;
 *     consumers that call `.detail(id)` always get the unfiltered endpoint.
 *   - `create()` and `item.add()` hardcode `saleChannel: "insideSalesIndividual"`.
 *   - `update()` and `item.update()` mutate the caller-provided `props` to
 *     add `saleChannel: "insideSalesIndividual"` (side effect — preserved
 *     because some upstream code may inspect the mutated object).
 *   - `checkout.create(data)` forwards `data.requestUUID` as the
 *     `x-acme-request-uuid` header (idempotency token for payment flows).
 */
const CARTS_BASE_V2 = '/v2/api/carts';
const CHECKOUT_BASE_V2 = '/v2/api/checkout';
const SALE_CHANNEL = 'insideSalesIndividual';

interface CartCreateProps {
  visitorId?: number | string;
  tempVisitorId?: string;
  membershipCategoryId?: number | string;
  couponCodes?: string[];
  items?: unknown[];
  forms?: unknown[];
  comboItems?: unknown[];
  membershipIds?: Array<number | string>;
  verifyEntitlements?: boolean;
}

interface CartUpdateProps {
  id: number | string;
  saleChannel?: string;
  [key: string]: unknown;
}

interface CartItemAddProps {
  eventId?: number | string;
  ticketingTypeId?: number | string;
  inventoryId?: number | string;
  quantity?: number;
  itemType?: string;
  addonType?: string;
  membershipInfo?: unknown;
  membershipId?: number | string;
  couponCodes?: string[];
  admissionType?: string;
  range?: unknown;
  isRetail?: boolean;
  eventName?: string;
  ticketingTypeName?: string;
  inventoryName?: string;
  unitPrice?: unknown;
  retailUnitPrice?: unknown;
  amount?: unknown;
  discountId?: number | string;
  donationDetail?: unknown;
  ignoreEntitlements?: boolean;
}

interface CartItemUpdateProps {
  itemId: number | string;
  saleChannel?: string;
  [key: string]: unknown;
}

interface CheckoutCreateProps {
  requestUUID?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class CartApiService {
  private readonly http = inject(HttpClient);

  // Legacy ignored the id; same here. Method signature documents the
  // historical caller expectation but the value is unused.
  list(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(CARTS_BASE_V2));
  }

  detail(_id?: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(CARTS_BASE_V2));
  }

  create(props: CartCreateProps): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(CARTS_BASE_V2, {
        visitorId: props.visitorId,
        tempVisitorId: props.tempVisitorId,
        membershipCategoryId: props.membershipCategoryId,
        couponCodes: props.couponCodes,
        items: props.items,
        forms: props.forms,
        comboItems: props.comboItems,
        membershipIds: props.membershipIds,
        saleChannel: SALE_CHANNEL,
        verifyEntitlements: props.verifyEntitlements,
      }),
    );
  }

  validateCoupons(props: { shoppingCartId: number | string; couponCodes: string[] }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${CARTS_BASE_V2}/couponcode/validate`, {
        shoppingCartId: props.shoppingCartId,
        couponCodes: props.couponCodes,
      }),
    );
  }

  // Legacy mutates `props.saleChannel`. Preserved.
  update(props: CartUpdateProps): Promise<AjsResponse<unknown>> {
    props.saleChannel = SALE_CHANNEL;
    return wrapAjs(this.http.put<unknown>(`${CARTS_BASE_V2}/${props.id}`, props));
  }

  delete(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${CARTS_BASE_V2}/${id}`));
  }

  readonly item = {
    add: (cartId: number | string, props: CartItemAddProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${CARTS_BASE_V2}/${cartId}/items`, {
          eventId: props.eventId,
          ticketingTypeId: props.ticketingTypeId,
          inventoryId: props.inventoryId,
          quantity: props.quantity,
          itemType: props.itemType,
          addonType: props.addonType,
          membershipInfo: props.membershipInfo,
          membershipId: props.membershipId,
          couponCodes: props.couponCodes,
          admissionType: props.admissionType,
          range: props.range,
          isRetail: props.isRetail,
          eventName: props.eventName,
          ticketingTypeName: props.ticketingTypeName,
          inventoryName: props.inventoryName,
          unitPrice: props.unitPrice,
          retailUnitPrice: props.retailUnitPrice,
          amount: props.amount,
          discountId: props.discountId,
          donationDetail: props.donationDetail,
          saleChannel: SALE_CHANNEL,
          ignoreEntitlements: props.ignoreEntitlements,
        }),
      ),

    // Legacy mutates `props.saleChannel`. Preserved.
    update: (cartId: number | string, props: CartItemUpdateProps): Promise<AjsResponse<unknown>> => {
      props.saleChannel = SALE_CHANNEL;
      return wrapAjs(this.http.put<unknown>(`${CARTS_BASE_V2}/${cartId}/items/${props.itemId}`, props));
    },

    delete: (cartId: number | string, id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${CARTS_BASE_V2}/${cartId}/items/${id}`)),
  };

  readonly checkout = {
    create: (data: CheckoutCreateProps): Promise<AjsResponse<unknown>> => {
      const headers = data.requestUUID ? new HttpHeaders({ 'x-acme-request-uuid': data.requestUUID }) : undefined;
      return wrapAjs(this.http.post<unknown>(CHECKOUT_BASE_V2, data, { headers }));
    },
  };
}
